import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { Coffee, Leaf, Dumbbell, Shield, Sprout, Zap, Users, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistent per-browser identifier used to soft-dedupe waitlist joins.
const FP_KEY = "sa_nutrition_fp";
const JOINED_KEY = "sa_nutrition_joined";
function getFingerprint() {
  let fp = localStorage.getItem(FP_KEY);
  if (!fp) {
    fp = `fp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(FP_KEY, fp);
  }
  return fp;
}

const PRODUCTS = [
  {
    icon: Coffee,
    name: "Liver Coffee™",
    tagline: "Support Metabolism. Protect the Liver.",
    body:
      "A premium functional coffee formulated with carefully selected, evidence-informed ingredients to support liver function, metabolic health, and everyday wellbeing.",
    testid: "product-liver-coffee",
  },
  {
    icon: Leaf,
    name: "Fiber Matrix™",
    tagline: "Nourish the Gut. Support the Liver.",
    body:
      "A scientifically formulated fibre blend designed to promote a healthy gut microbiome, improve digestive health, and strengthen the gut–liver connection for better metabolic health.",
    testid: "product-fiber-matrix",
  },
  {
    icon: Dumbbell,
    name: "Protein Balance™",
    tagline: "Build Muscle. Improve Metabolism. Age Better.",
    body:
      "A high-quality protein formulation designed to preserve lean muscle mass, support healthy ageing, and optimize metabolic health — because a healthy liver starts with healthy muscles.",
    testid: "product-protein-balance",
  },
];

const PILLARS = [
  {
    icon: Shield,
    title: "Protect the Liver",
    body: "Reduce liver stress through smarter nutrition and healthier lifestyle choices.",
  },
  {
    icon: Sprout,
    title: "Feed the Gut",
    body: "Support the gut microbiome and strengthen the gut–liver axis.",
  },
  {
    icon: Zap,
    title: "Build the Muscle",
    body: "Preserve muscle, improve metabolic health, and promote healthy ageing.",
  },
];

export const PrecisionNutrition = () => {
  const [count, setCount] = useState(41);
  const [displayCount, setDisplayCount] = useState(0);
  const [joined, setJoined] = useState(false);
  const [busy, setBusy] = useState(false);
  const counterRef = useRef(null);

  // Load initial count + joined state
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/nutrition/waitlist`).then((r) => {
      if (alive) setCount(r.data?.count ?? 41);
    }).catch(() => {});
    setJoined(localStorage.getItem(JOINED_KEY) === "1");
    return () => { alive = false; };
  }, []);

  // Count-up animation whenever `count` changes (and card visible)
  useEffect(() => {
    let raf;
    const from = displayCount;
    const to = count;
    if (from === to) return;
    const duration = 1200;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayCount(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const handleJoin = async () => {
    if (joined || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/nutrition/waitlist`, {
        fingerprint: getFingerprint(),
      });
      setCount(r.data?.count ?? count + 1);
      localStorage.setItem(JOINED_KEY, "1");
      setJoined(true);
    } catch {
      /* silent */
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="precision-nutrition"
      className="relative overflow-hidden bg-sa-bg py-16 md:py-20"
      data-testid="precision-nutrition-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-sa-copper/15 px-4 py-1.5 text-[13px] font-semibold uppercase tracking-wider text-sa-copper"
            data-testid="precision-nutrition-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sa-copper opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sa-copper" />
            </span>
            Launching 2027
          </span>
          <h2
            className="mt-6 font-display text-4xl font-medium leading-[1.06] tracking-tight text-sa-forest sm:text-5xl lg:text-6xl"
            data-testid="precision-nutrition-title"
          >
            Precision Nutrition
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-sa-muted md:text-xl">
            Physician-designed nutrition for healthier livers, stronger muscles, and better metabolic health.
          </p>
          <p className="mt-5 max-w-2xl mx-auto text-[15px] leading-relaxed text-sa-forest/75">
            At SuperAge Health™, nutrition should be personalized, evidence-based, and integrated into a
            comprehensive health strategy. Our Precision Nutrition range complements physician-guided
            care, healthy lifestyle habits, and long-term liver health — it does not replace them.
          </p>

          {/* Waitlist card */}
          <div
            ref={counterRef}
            className="mx-auto mt-10 inline-flex flex-col items-center gap-4 rounded-3xl border border-sa-beige bg-white px-6 py-5 shadow-[0_20px_50px_-30px_rgba(10,46,34,0.35)] sm:flex-row sm:gap-6 sm:px-8"
            data-testid="nutrition-waitlist-card"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sa-copper/15 text-sa-copper">
                <Users className="h-5 w-5" strokeWidth={2} />
              </span>
              <div className="text-left">
                <p
                  className="font-display text-2xl font-medium tracking-tight text-sa-forest sm:text-3xl"
                  data-testid="nutrition-waitlist-count"
                >
                  {displayCount.toLocaleString()}
                </p>
                <p className="text-[13px] text-sa-muted">
                  already on the waitlist
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleJoin}
              disabled={joined || busy}
              data-testid="nutrition-waitlist-join"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-all duration-300 ${
                joined
                  ? "bg-sa-forest/10 text-sa-forest cursor-default"
                  : busy
                  ? "bg-sa-forest/60 text-white cursor-wait"
                  : "bg-sa-forest text-white hover:bg-sa-forest/90 hover:shadow-lg"
              }`}
            >
              {joined ? (
                <>
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                  You're on the list
                </>
              ) : busy ? (
                "Joining…"
              ) : (
                "Join the Waitlist"
              )}
            </button>
          </div>
        </Reveal>

        {/* Products */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8"
        >
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.name}
                variants={fadeUp}
                data-testid={p.testid}
                className="group relative flex flex-col overflow-hidden rounded-3xl border border-sa-beige bg-white p-8 transition-shadow duration-500 hover:shadow-[0_20px_50px_-25px_rgba(10,46,34,0.35)]"
              >
                <span className="absolute right-6 top-6 rounded-full bg-sa-forest/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sa-forest/70">
                  Launching Soon
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sa-copper/15 text-sa-copper">
                  <Icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-medium text-sa-forest">
                  {p.name}
                </h3>
                <p className="mt-2 font-medium text-sa-copper">{p.tagline}</p>
                <p className="mt-4 text-[15px] leading-relaxed text-sa-forest/75">
                  {p.body}
                </p>
              </motion.article>
            );
          })}
        </motion.div>

        {/* Philosophy — three pillars */}
        <Reveal className="mt-20 rounded-3xl bg-sa-forest p-10 text-white md:p-14">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-sa-copper/20 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-wider text-sa-copper">
              The Philosophy
            </span>
            <h3 className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl">
              The SuperAge Precision Nutrition Philosophy
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-white/70">
              Every formulation is built around three evidence-based pillars.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3"
          >
            {PILLARS.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={pillar.title}
                  variants={fadeUp}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                  data-testid={`nutrition-pillar-${pillar.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sa-copper/20 text-sa-copper">
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <h4 className="mt-4 font-display text-xl font-medium text-white">
                    {pillar.title}
                  </h4>
                  <p className="mt-2 text-[14px] leading-relaxed text-white/70">
                    {pillar.body}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
};

export default PrecisionNutrition;
